import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Truck, DollarSign, CalendarOff, Wallet, MessageSquare, AlertTriangle,
  Mail, Smartphone, Save, Loader2, Phone, Layers,
} from 'lucide-react';
import { toast as sonner } from './ui/sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_META = {
  load:     { icon: Truck,         label: 'Load updates',        desc: 'Assignments, status changes, route updates' },
  payroll:  { icon: DollarSign,    label: 'Payroll',             desc: 'Paystubs published, correction decisions' },
  deferred: { icon: Layers,        label: 'Deferred deductions', desc: 'When a deduction is deferred to your next paycheck' },
  leave:    { icon: CalendarOff,   label: 'Time-off',            desc: 'Leave submitted, approved, or denied' },
  advance:  { icon: Wallet,        label: 'Cash advances',       desc: 'Advance requests submitted or decided' },
  message:  { icon: MessageSquare, label: 'Messages',            desc: 'Load chat & direct messages' },
  system:   { icon: AlertTriangle, label: 'System alerts',       desc: 'Document expiries, billing, security' },
};

const CHANNEL_META = {
  email: { icon: Mail,       label: 'Email' },
  sms:   { icon: Smartphone, label: 'SMS' },
};

/**
 * NotificationPreferences
 * ───────────────────────
 * Two-channel × six-category opt-in matrix.  In-app bell notifications are
 * always on — this card only governs how Resend (email) and Twilio (SMS)
 * channels are gated when they ship.
 *
 * Renders inline inside the Profile page (no separate route).
 */
const NotificationPreferences = ({ userEmail }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [prefs, setPrefs] = useState(null);
  const [phone, setPhone] = useState('');
  const [phoneSaving, setPhoneSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [testEmailSending, setTestEmailSending] = useState(false);
  const [testSmsSending, setTestSmsSending] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/notification-preferences/me`, { withCredentials: true });
        if (cancelled) return;
        setPrefs(res.data.preferences);
        setPhone(res.data.phone || '');
      } catch {
        sonner.error('Failed to load notification preferences');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const toggle = (cat, ch) => {
    setPrefs((prev) => {
      const base = prev || {};
      const catPrev = base[cat] || {};
      return {
        ...base,
        [cat]: { ...catPrev, [ch]: !catPrev[ch] },
      };
    });
    setDirty(true);
  };

  const handleSavePrefs = async () => {
    if (!prefs) return;
    setSaving(true);
    try {
      const res = await axios.put(
        `${API}/notification-preferences/me`,
        { preferences: prefs },
        { withCredentials: true },
      );
      setPrefs(res.data.preferences);
      setDirty(false);
      sonner.success('Notification preferences saved');
    } catch (err) {
      sonner.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleSavePhone = async () => {
    setPhoneSaving(true);
    try {
      const res = await axios.put(
        `${API}/auth/profile`,
        { phone },
        { withCredentials: true },
      );
      setPhone(res.data.phone || '');
      sonner.success('Phone number saved');
    } catch (err) {
      sonner.error(err.response?.data?.detail || 'Invalid phone number');
    } finally {
      setPhoneSaving(false);
    }
  };

  const handleSendTestEmail = async () => {
    setTestEmailSending(true);
    try {
      const res = await axios.post(`${API}/notifications/test-email`, {}, { withCredentials: true });
      sonner.success(`Test email sent to ${res.data.sent_to}`);
    } catch (err) {
      sonner.error(err.response?.data?.detail || 'Failed to send test email');
    } finally {
      setTestEmailSending(false);
    }
  };

  const handleSendTestSms = async () => {
    setTestSmsSending(true);
    try {
      const res = await axios.post(`${API}/notifications/test-sms`, {}, { withCredentials: true });
      sonner.success(`Test SMS sent to ${res.data.sent_to}`);
    } catch (err) {
      sonner.error(err.response?.data?.detail || 'Failed to send test SMS');
    } finally {
      setTestSmsSending(false);
    }
  };

  const channelEnabled = (ch) => {
    if (!prefs) return false;
    return Object.values(prefs).some((c) => c[ch]);
  };

  const smsAllOn = channelEnabled('sms');

  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-md p-6 mt-6 flex items-center justify-center text-slate-500 text-sm">
        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading preferences…
      </div>
    );
  }

  return (
    <div data-testid="notification-preferences" className="bg-white border border-slate-200 rounded-md p-6 mt-6">
      <div className="flex items-start justify-between gap-4 mb-1">
        <h2 className="text-lg font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
          Notification Preferences
        </h2>
      </div>
      <p className="text-xs text-slate-500 mb-5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
        In-app bell is always on. Choose extra channels per topic — quiet what you don't need.
      </p>

      {/* Phone number row (used only when SMS is enabled anywhere) */}
      <div
        data-testid="notification-prefs-phone-row"
        className={`flex items-center gap-3 p-3 rounded-md border mb-5 transition-colors ${smsAllOn ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200'}`}
      >
        <Phone className={`w-4 h-4 shrink-0 ${smsAllOn ? 'text-amber-600' : 'text-slate-400'}`} />
        <div className="flex-1 min-w-0">
          <label className="block text-[11px] font-semibold uppercase tracking-widest text-slate-600 mb-1">
            Mobile number {smsAllOn && <span className="text-amber-700 normal-case font-normal">— required for SMS</span>}
          </label>
          <input
            data-testid="notification-prefs-phone-input"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+1 555 123 4567"
            className="w-full px-2 py-1 text-sm bg-white border border-slate-200 rounded focus:outline-none focus:border-[#002FA7]"
            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
          />
        </div>
        <button
          data-testid="notification-prefs-phone-save"
          onClick={handleSavePhone}
          disabled={phoneSaving}
          className="px-3 py-1.5 text-xs font-semibold text-[#002FA7] hover:bg-[#002FA7] hover:text-white border border-[#002FA7] rounded-md transition-colors disabled:opacity-50"
          style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
        >
          {phoneSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
        </button>
      </div>

      {/* Matrix */}
      <div className="overflow-x-auto" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-slate-500">
              <th className="text-left font-semibold uppercase tracking-widest py-2 pr-3">Topic</th>
              {Object.entries(CHANNEL_META).map(([key, m]) => {
                const Icon = m.icon;
                return (
                  <th key={key} className="text-center font-semibold uppercase tracking-widest py-2 px-2 w-24">
                    <div className="flex items-center justify-center gap-1.5">
                      <Icon className="w-3.5 h-3.5" /> {m.label}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {Object.entries(CATEGORY_META).map(([cat, meta]) => {
              const Icon = meta.icon;
              return (
                <tr key={cat} className="hover:bg-slate-50">
                  <td className="py-3 pr-3">
                    <div className="flex items-start gap-2.5">
                      <Icon className="w-4 h-4 text-[#002FA7] mt-0.5 shrink-0" strokeWidth={2} />
                      <div>
                        <div className="font-semibold text-slate-900 text-sm">{meta.label}</div>
                        <div className="text-xs text-slate-500">{meta.desc}</div>
                      </div>
                    </div>
                  </td>
                  {Object.keys(CHANNEL_META).map((ch) => {
                    const on = Boolean(prefs?.[cat]?.[ch]);
                    return (
                    <td key={ch} className="text-center py-3 px-2">
                      <button
                        data-testid={`pref-toggle-${cat}-${ch}`}
                        onClick={() => toggle(cat, ch)}
                        role="switch"
                        aria-checked={on}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-150 ${on ? 'bg-[#002FA7]' : 'bg-slate-300'}`}
                      >
                        <span
                          className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow transform transition-transform duration-150 ${on ? 'translate-x-5' : 'translate-x-1'}`}
                        />
                      </button>
                    </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between mt-6 gap-3 flex-wrap">
        <div className="text-[11px] text-slate-500">
          {userEmail && (
            <>Email will be sent to <span className="font-semibold text-slate-700">{userEmail}</span></>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            data-testid="send-test-email"
            type="button"
            onClick={handleSendTestEmail}
            disabled={testEmailSending}
            title="Send a one-off test email to confirm your email channel works"
            className="border border-slate-300 text-slate-700 hover:bg-slate-100 px-3 py-2 text-xs rounded-md font-semibold transition-all disabled:opacity-50 flex items-center gap-1.5"
            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
          >
            {testEmailSending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
            {testEmailSending ? 'Sending…' : 'Test email'}
          </button>
          <button
            data-testid="send-test-sms"
            type="button"
            onClick={handleSendTestSms}
            disabled={testSmsSending || !phone}
            title={phone ? 'Send a one-off test SMS to confirm your SMS channel works' : 'Save a mobile number first'}
            className="border border-slate-300 text-slate-700 hover:bg-slate-100 px-3 py-2 text-xs rounded-md font-semibold transition-all disabled:opacity-50 flex items-center gap-1.5"
            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
          >
            {testSmsSending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Smartphone className="w-3.5 h-3.5" />}
            {testSmsSending ? 'Sending…' : 'Test SMS'}
          </button>
          <button
            data-testid="notification-prefs-save"
            onClick={handleSavePrefs}
            disabled={saving || !dirty}
            className="bg-[#002FA7] text-white px-5 py-2 text-sm rounded-md font-semibold hover:bg-[#00247D] transition-all disabled:opacity-50 flex items-center gap-2"
            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? 'Saving…' : 'Save Preferences'}
          </button>
        </div>
      </div>

      <p className="text-[10px] text-slate-400 mt-4 italic">
        Email and SMS delivery are both live. Your preferences are honored the moment each
        notification fires — unsubscribe any topic at any time.
      </p>
    </div>
  );
};

export default NotificationPreferences;
